import { Paginated } from "@feathersjs/feathers/lib";
import { action, computed, makeObservable, observable, runInAction, toJS } from "mobx";
import { DataType, IColumn, ITask, Services, TaskType, TaskValidationError } from "types";
import { Choice, Content } from ".";
import client from "../feathers";


export class Task extends Content<ITask> implements ITask {

  course_id:   number   = null;
  question:    string   = null;
  quiz_id:     number   = null;
  step_id:     number   = null;
  type:        TaskType = null;
  num_choice:  number   = null;
  always_show: boolean  = null;
  answer?:     string   = null;
  choices?:    Choice[] = [];
  columns?:    IColumn[] = [];
  selection?:  number[] = []; // choice ids of the selected answers
  input?:      string   = null;
  showAnswer:  boolean  = false;
  correct_feedback?:   string = null;
  incorrect_feedback?: string = null;
  loading:     boolean  = false;
  
  constructor(data: ITask) {
    // The super constructor takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Task, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      num_choice:  observable,
      always_show: observable,
      course_id:   observable,
      question:    observable,
      quiz_id:     observable,
      step_id:     observable,
      type:        observable,
      choices:     observable,
      columns:     observable,
      selection:   observable,
      input:       observable,
      answer:      observable,
      showAnswer:  observable,
      correct_feedback:   observable,
      incorrect_feedback: observable,

      feedback:           computed,
      getSelected:        computed,
      isComplete:         computed,
      getValidationError: computed,
      isValid:            computed,

      toggleSelect:  action,
      setShowAnswer: action,
      reset:         action,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);
  }
  /*******************************************************************************************************************************************
   * Database Access
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/

  public static get = async (id?: number): Promise<Task | Task[]> => {
    try {
      if(id) {
        return Task.create(await client.service(Services.Task).get(id));
      } else {
        const tasks: Paginated<ITask> = await client.service(Services.Task).find();
        if(!tasks.total) return [];
        return tasks.data.map(q => Task.create(q));
      }
    } catch (err) {
      console.error("Failed to load Taskes", err);
      return null;
    }
  }

  public static create(data: ITask): Task {
    return new Task(data);
  }

  public get feedback(): any[] {
    return [
      {
        wrong: 0,
        text: this.correct_feedback,
      },
      {
        wrong: 1,
        text: this.incorrect_feedback,
      },
    ]
  }

  protected fromData(data: ITask): void {
    super.fromData(data);
    this.course_id   = data.course_id && Number(data.course_id);
    this.quiz_id     = data.quiz_id && Number(data.quiz_id);
    this.step_id     = data.step_id && Number(data.step_id);
    this.always_show = data.always_show;
    this.num_choice  = data.num_choice;
    this.question    = data.question;
    this.type        = data.type;
    this.answer      = data.answer;
    this.columns     = (data.columns||[]).map(c => JSON.parse(c.toString()));
    this.choices     = (data.choices && data.choices.map(c => Choice.create(c)))?.sort((a, b) => a.id - b.id) || this.choices || [];
    this.correct_feedback   = data.correct_feedback;
    this.incorrect_feedback = data.incorrect_feedback;
  }

  public toData(): any {
    return {
      ...super.toData(),
      num_choice:  this.num_choice || null,
      always_show: this.always_show || null,
      course_id:   this.course_id || null,
      question:    this.question || null,
      quiz_id:     this.quiz_id || null,
      step_id:     this.step_id || null,
      type:        this.type || null,
      correct_feedback:   this.correct_feedback || null,
      incorrect_feedback: this.incorrect_feedback || null,
      columns:     toJS([...this.columns||[]].slice()),
    };
  }

  public toPatchData(patch: Partial<ITask>): any {
    return {
      ...super.toPatchData(patch),
      always_show: "always_show" in patch ? this.always_show: undefined,
      num_choice:  "num_choice" in patch ? this.num_choice:   undefined,
      course_id:   "course_id" in patch ? this.course_id:     undefined,
      question:    "question" in patch ? this.question:       undefined,
      quiz_id:     "quiz_id" in patch ? this.quiz_id:         undefined,
      step_id:     "step_id" in patch ? this.step_id:         undefined,
      type:        "type" in patch ? this.type:               undefined,
      answer:      "answer" in patch ? this.answer:           undefined,
      correct_feedback:   "correct_feedback" in patch ? this.correct_feedback:     undefined,
      incorrect_feedback: "incorrect_feedback" in patch ? this.incorrect_feedback: undefined,
      columns: "columns" in patch ? toJS([...this.columns||[]].slice()) : undefined,
    };
  }

  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/
  public get canEdit(): boolean {
    return true;
  }

  /**
   * @return true if the task is valid and ready to be published by the editor.
   *         
   * Note: Use @getValidationError to receive the error message that explains 
   *       why the task is invalid. 
   * 
   * A task is valid if:
   * - it has a question
   * Input tasks:
   * - it has a none empty answer
   * Select Tasks:
   * - it has at least as many choices as @num_choice
   * - at least one choice is marked as correct
   * - for single select: it has at least @num_choice - 1 incorrect tasks
   *   to display one correct and only incorrect otherwise. 
   * - all choices have a text
   */
  public get isValid(): boolean { 
    // Question is set
    if(!(this.question && this.question.length)) return false;
    // Answer for input tasks is set
    if(this.type === TaskType.Input && !Boolean(this.answer?.length)) return false;
    if(this.type == TaskType.Column) {
      if(this.columns?.some(c => !c.text || !c.text.length)) return false;
      if(this.choices?.some(c => !c.column)) return false;
      return true;
    }
    if(this.type !== TaskType.Input) {
      // Multiple choice questions have sufficient choices
      if(this.num_choice > this.choices.length) return false;
      // All choices have a text
      if(this.choices.some(c => !c.text || !c.text.length)) return false;
      // The task has at least one correct answer
      if(!this.choices.some(c => c.is_correct)) return false;
      // Single select tasks have sufficiently man incorrect questions as well
      if(
        this.type === TaskType.Single && 
        this.choices.filter(c => !c.is_correct).length < (this.num_choice - 1)
      ) return false;
    } 
    return true;
  }

  /**
   * @return validation error message key to display information about 
   *         why the task is invalid. 
   */
  public get getValidationError(): TaskValidationError {
    if(!this.question || !this.question.length)
      return TaskValidationError.EmptyQuestion;

    if(this.type === TaskType.Column) {
      if(this.columns?.some(c => !c.text || !c.text.length)) {
        return TaskValidationError.EmptyChoice;
      }
      if(this.choices?.some(c => !c.column)) {
        return TaskValidationError.EmptyChoice;
      }
    }
    else if(this.type === TaskType.Input) {
      if(!this.answer?.length)
        return TaskValidationError.EmptyAnswer;
    } else {
      if(this.choices.some(c => !c.text || !c.text.length))
        return TaskValidationError.EmptyChoice;
      else if(this.num_choice  > this.choices.length)
        return TaskValidationError.InsufficientChoices;
      else if(!this.choices.filter(c => c.is_correct).length)
        return TaskValidationError.NoCorrectChoice;
      else if(this.type === TaskType.Single && this.choices.filter(c => !c.is_correct).length < (this.num_choice - 1))
        return TaskValidationError.InsufficientIncorrectChoices;
    }

    return TaskValidationError.Valid;
  }

  public get getSelected(): Choice[] {
    return this.choices.filter(c => c.is_selected);
  }

  /**
   * @return <true> if the task has at least one selected choice or,
   *         in the case of an input task, the user input is not empty.
   */
  public get isComplete(): boolean {
    if(this.type === TaskType.Input)  return Boolean(this.input?.length);
    if(this.type === TaskType.Column) return true; // TODO: check if all columns are filled if the step is mandatory
    return Boolean(this.getSelected.length);
  }

  public get isAnswerCorrect(): boolean {
    switch (this.type) {
      case TaskType.Single:
        return this.choices.some(c => c.is_selected && c.is_correct);
      case TaskType.Column:
        return this.input == 'finished';
      case TaskType.Multi:
        return !this.choices.some(c => c.is_correct && !c.is_selected) &&
          !this.choices.some(c => !c.is_correct && c.is_selected);
      case TaskType.Input:
        return this.answer === this.input;
      default: break;
    }
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/
  toggleSelect = (choice: Choice) => {
    if(this.type === TaskType.Single) this.getSelected.forEach(c => c.toggleSelect());
    choice.toggleSelect();
  }

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/

  addChoice = async (): Promise<Choice> => {
    try {
      const choice: Choice = Choice.create({
        course_id:  this.course_id,
        quiz_id:    this.quiz_id,
        step_id:    this.step_id,
        task_id:    this.id,
        is_correct: false,
        text:       '',
      });
      await choice.save();
      runInAction(() => this.choices.push(choice));
      return choice;
    } catch (err) {
      console.error("Failed to add task", err);
      return null;
    }
  }

  removeChoice = async (choice: Choice): Promise<boolean> => {
    try {
      await choice.remove();
      runInAction(() => {
        this.choices = this.choices.filter(c => c.id !== choice.id);
      })
    } catch(err) {
      console.error("Failed to remove choice", err);
      return false;
    }
    return true;
  }

  addColumn = async (): Promise<IColumn> => {
    try {
      const i = Math.floor(Math.random() * 1000000) + 1;      ;
      const column: IColumn = {
        index: i,
        title: ``,
        text:  ``,
      };
      
      this.set('columns', [...(this.columns||[]), column], true);
      return column;
    } catch (err) {
      console.error("Failed to add column", err);
      return null;
    }
  }

  removeColumn = async (column: IColumn, save?:boolean): Promise<boolean> => {
    try {
        const updateCols = (this.choices||[]).map(c => {
          if(c.column === column.index) {
            return c.set('column', null, true);  
          }
        });
      
      await Promise.all(updateCols);
      
      await this.set('choices', this.choices, true);

      await this.set("columns", [...this.columns.filter(c => c.index !== column.index)], save);
      console.log(2, this.columns?.length)
    } catch(err) {
      console.error("Failed to remove column", err);
      return false;
    }   
  }

  setColumnText = (column: IColumn, text: string, save?: boolean) => {
    const columns = this.columns||[];
    columns.find(c => c.index === column.index).text = text;
    this.set("columns", [...columns], save);

  }

  setColumnTitle = (column: IColumn, text: string, save?: boolean) => {
    const columns = this.columns||[];
    columns.find(c => c.index === column.index).title = text;
    console.log(columns)
    this.set("columns", [...columns], save);
  }

  setShowAnswer = (show: boolean) => {
    this.showAnswer = show;
  }

  reset = () => {
    this.input      = null;
    this.selection  = [];
    this.showAnswer = false;
    this.choices.forEach(c => c.is_selected = false);
  }
}
